// P1-8: the fourth running count the requirements ask for -- Duplicate (spec §2.1).
//
// Deferred from step 3 of the execution order on purpose: a duplicate count is only
// worth showing once P1-3 makes detection deterministic, and until this branch it
// wasn't. See utilities/scanMatch.js.
//
// ── WHY A SESSION COUNTER, AND NOT SERVER-SIDE ───────────────────────────────
// The other four tiles come from one server-side definition (P1-2,
// CC_Backend/utils/stats.js) because they describe rows that exist. A duplicate is
// a scan that was REFUSED -- nothing is persisted, so there is nothing for the
// server to count. Remediation Plan v2 §3 defines it as "a running tally of
// rejected duplicate scan attempts (session counter)", and spec §4.2/§4.3 do not
// list it among the report counts.
//
// So it deliberately does NOT go into the `stats` object: putting a client-only key
// in a server-authoritative shape would imply it is reportable, and someone would
// then put it in the XLS. If the business later wants duplicates on the report,
// that needs a persisted record (a migration), not a counter -- see
// REMEDIATION_PROGRESS.md.
//
// ── WHY A MODULE AND NOT useState(0) ────────────────────────────────────────
// The tally carries the collection id it belongs to, so the per-order rule
// (spec §5.2) is structural: it is not possible to render one order's duplicate
// count against another. Switching orders starts a fresh tally rather than
// carrying a stale number across. A bare counter would need the caller to remember
// to reset it, which is exactly the kind of thing that gets missed on one of
// several code paths -- the shape of the P1-17 bug.

// Newest first. Capped because a long scanning session on a badly duplicated pallet
// should not grow unboundedly in memory; the count itself is never capped.
const MAX_TALLY_EVENTS = 50;

const EMPTY_TALLY = {
    collectionId: null,
    count: 0,
    events: []
};

const forCollection = (collectionId) => ({
    collectionId: collectionId ?? null,
    count: 0,
    events: []
});

// Same loose comparison as scanMatch.assetsInOrder: ids arrive as a number from
// redux and a string from the route params.
const sameCollection = (tally, collectionId) =>
    tally != null && String(tally.collectionId) === String(collectionId);

/**
 * Record one rejected duplicate scan. Pure -- returns a new tally.
 *
 * A tally belonging to a different order is replaced rather than added to, so the
 * count always describes the order on screen.
 */
const recordDuplicate = (tally, event = {}) => {
    const { collectionId, serial = '', assetTag = '', assetId = null, at = null, source = null } = event;

    const current = sameCollection(tally, collectionId) ? tally : forCollection(collectionId);

    return {
        collectionId: collectionId ?? null,
        count: current.count + 1,
        events: [
            { serial, assetTag, assetId, at, source },
            ...current.events
        ].slice(0, MAX_TALLY_EVENTS)
    };
};

// Zero unless the tally belongs to this order -- never show another order's number.
const duplicateCount = (tally, collectionId) =>
    (sameCollection(tally, collectionId) ? tally.count : 0);

const duplicateEvents = (tally, collectionId) =>
    (sameCollection(tally, collectionId) ? tally.events : []);

const exports = {
    EMPTY_TALLY,
    MAX_TALLY_EVENTS,
    recordDuplicate,
    duplicateCount,
    duplicateEvents,
};

export default exports;
export {
    EMPTY_TALLY,
    MAX_TALLY_EVENTS,
    recordDuplicate,
    duplicateCount,
    duplicateEvents,
};
